/**
 * MonthPicker — Dark-themed month/year picker matching app aesthetic
 *
 * Replaces the browser-default <input type="month"> which renders as a stark
 * white dialog that clashes with the app's dark palette. Emits/consumes
 * 'YYYY-MM' strings to stay drop-in compatible.
 */

import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import { Calendar, ChevronLeft, ChevronRight, X as XIcon } from 'lucide-react';

interface MonthPickerProps {
  value: string;
  onChange: (next: string) => void;
  placeholder?: string;
  className?: string;
  disabled?: boolean;
  minMonth?: string;
  maxMonth?: string;
}

const MONTH_ABBR = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
];
const MONTH_FULL = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

function parseValue(value: string): { year: number; month: number } | null {
  if (!value) return null;
  const match = /^(\d{4})-(0[1-9]|1[0-2])$/.exec(value);
  if (!match) return null;
  return { year: parseInt(match[1], 10), month: parseInt(match[2], 10) };
}

function formatValue(year: number, month: number): string {
  return `${year}-${String(month).padStart(2, '0')}`;
}

function formatDisplay(value: string): string {
  const parsed = parseValue(value);
  if (!parsed) return '';
  return `${MONTH_FULL[parsed.month - 1]} ${parsed.year}`;
}

function compareMonth(a: string, b: string): number {
  return a.localeCompare(b);
}

const MonthPicker: React.FC<MonthPickerProps> = ({
  value,
  onChange,
  placeholder = 'Select month',
  className = '',
  disabled = false,
  minMonth,
  maxMonth,
}) => {
  const [isOpen, setIsOpen] = useState(false);

  const [viewYear, setViewYear] = useState<number>(() => {
    const parsed = parseValue(value);
    if (parsed) return parsed.year;
    return new Date().getFullYear();
  });

  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const parsed = parseValue(value);
    if (parsed) setViewYear(parsed.year);
  }, [value]);

  useEffect(() => {
    if (!isOpen) return;

    const onDocClick = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setIsOpen(false);
    };

    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [isOpen]);

  const selected = useMemo(() => parseValue(value), [value]);
  const now = useMemo(() => {
    const d = new Date();
    return { year: d.getFullYear(), month: d.getMonth() + 1 };
  }, []);

  const handleSelect = useCallback(
    (month: number) => {
      const next = formatValue(viewYear, month);
      if (minMonth && compareMonth(next, minMonth) < 0) return;
      if (maxMonth && compareMonth(next, maxMonth) > 0) return;
      onChange(next);
      setIsOpen(false);
    },
    [viewYear, minMonth, maxMonth, onChange]
  );

  const handleClear = useCallback(() => {
    onChange('');
    setIsOpen(false);
  }, [onChange]);

  const handleThisMonth = useCallback(() => {
    const next = formatValue(now.year, now.month);
    if (minMonth && compareMonth(next, minMonth) < 0) return;
    if (maxMonth && compareMonth(next, maxMonth) > 0) return;
    setViewYear(now.year);
    onChange(next);
    setIsOpen(false);
  }, [now, minMonth, maxMonth, onChange]);

  const isMonthDisabled = useCallback(
    (month: number) => {
      const candidate = formatValue(viewYear, month);
      if (minMonth && compareMonth(candidate, minMonth) < 0) return true;
      if (maxMonth && compareMonth(candidate, maxMonth) > 0) return true;
      return false;
    },
    [viewYear, minMonth, maxMonth]
  );

  return (
    <div ref={rootRef} className={`relative ${className}`}>
      <button
        type="button"
        onClick={() => !disabled && setIsOpen((o) => !o)}
        disabled={disabled}
        className={`w-full flex items-center justify-between gap-3 bg-gray-700 border rounded-lg px-3 py-2.5 text-sm text-left transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
          disabled
            ? 'border-gray-700 text-gray-500 cursor-not-allowed'
            : isOpen
            ? 'border-blue-500 text-white'
            : 'border-gray-600 text-white hover:border-gray-500'
        }`}
        aria-haspopup="dialog"
        aria-expanded={isOpen}
      >
        <span className="flex items-center gap-2 truncate">
          <Calendar className="w-4 h-4 text-gray-400 flex-shrink-0" />
          {value ? (
            <span className="font-semibold">{formatDisplay(value)}</span>
          ) : (
            <span className="text-gray-500">{placeholder}</span>
          )}
        </span>
        {value && !disabled && (
          <span
            role="button"
            tabIndex={0}
            onClick={(e) => {
              e.stopPropagation();
              handleClear();
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.stopPropagation();
                e.preventDefault();
                handleClear();
              }
            }}
            className="flex items-center text-gray-500 hover:text-gray-300 flex-shrink-0 cursor-pointer"
            aria-label="Clear"
          >
            <XIcon className="w-3.5 h-3.5" />
          </span>
        )}
      </button>

      {isOpen && !disabled && (
        <div
          className="absolute z-50 mt-2 w-72 bg-gray-800 border border-gray-700 rounded-lg shadow-2xl overflow-hidden"
          role="dialog"
          aria-label="Select month"
        >
          <div className="flex items-center justify-between px-3 py-2.5 border-b border-gray-700 bg-gray-900">
            <button
              type="button"
              onClick={() => setViewYear((y) => y - 1)}
              className="p-1 rounded text-gray-400 hover:text-white hover:bg-gray-700 transition-colors"
              aria-label="Previous year"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
            <span className="text-white text-sm font-semibold tracking-wider">
              {viewYear}
            </span>
            <button
              type="button"
              onClick={() => setViewYear((y) => y + 1)}
              className="p-1 rounded text-gray-400 hover:text-white hover:bg-gray-700 transition-colors"
              aria-label="Next year"
            >
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>

          <div className="grid grid-cols-4 gap-1.5 p-3">
            {MONTH_ABBR.map((label, i) => {
              const monthNum = i + 1;
              const isSelected =
                !!selected && selected.year === viewYear && selected.month === monthNum;
              const isCurrent = now.year === viewYear && now.month === monthNum;
              const disabledCell = isMonthDisabled(monthNum);

              return (
                <button
                  key={monthNum}
                  type="button"
                  onClick={() => handleSelect(monthNum)}
                  disabled={disabledCell}
                  className={`relative px-2 py-2 rounded text-xs font-medium transition-colors ${
                    disabledCell
                      ? 'text-gray-600 cursor-not-allowed'
                      : isSelected
                      ? 'bg-blue-600 text-white shadow-md'
                      : isCurrent
                      ? 'bg-gray-700/50 text-blue-400 hover:bg-gray-700'
                      : 'text-gray-300 hover:bg-gray-700 hover:text-white'
                  }`}
                >
                  {label}
                  {isCurrent && !isSelected && (
                    <span className="absolute bottom-0.5 left-1/2 -translate-x-1/2 w-1 h-1 rounded-full bg-blue-400" />
                  )}
                </button>
              );
            })}
          </div>

          <div className="flex items-center justify-between px-3 py-2 border-t border-gray-700 bg-gray-900/50">
            <button
              type="button"
              onClick={handleClear}
              className="text-xs font-medium text-gray-400 hover:text-white transition-colors"
            >
              Clear
            </button>
            <button
              type="button"
              onClick={handleThisMonth}
              className="text-xs font-medium text-blue-400 hover:text-blue-300 transition-colors"
            >
              This month
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default MonthPicker;
